import {useCallback, useEffect} from 'react';
import {create} from 'zustand';
import {WS_EVENT_TYPES} from '@chekin/api';
import {useGuest} from 'hooks';
import {useGetGuestId} from 'hooks/useGetGuestId';
import {useWebsocket} from 'context/websocket';
import {useRemoteIVFlow} from 'hooks/iv/ivRemoteFlow';
import {useIVRemoteSync, getIVIntentRemoteSession} from 'hooks/iv/useIVRemoteSync';

type IVRemoteHandoffStore = {
  isStartedOnMobile: boolean;
  setStartedOnMobile: (value: boolean) => void;
};

export const useIVRemoteHandoffStore = create<IVRemoteHandoffStore>(set => ({
  isStartedOnMobile: false,
  setStartedOnMobile: value => set({isStartedOnMobile: value}),
}));

export function useIVRemoteEvents() {
  const id = useGetGuestId();
  const {guest} = useGuest({id});
  const {loadRemoteState} = useIVRemoteSync();
  const {goToGuestForm, goToHome, goToGuestRegistration} = useRemoteIVFlow();
  const setStartedOnMobile = useIVRemoteHandoffStore(state => state.setStartedOnMobile);
  const websocket = useWebsocket();

  const handleReturningToDesktop = useCallback(() => {
    if (guest) {
      goToGuestRegistration();
    } else {
      void loadRemoteState({postHandler: goToGuestForm});
    }
  }, [goToGuestForm, goToGuestRegistration, guest, loadRemoteState]);

  useEffect(() => {
    const message = websocket.message;
    const intentId = getIVIntentRemoteSession();
    if (!message?.event_type || intentId !== message.instance_id) return;

    if (WS_EVENT_TYPES.ivStartedOnMobile === message.event_type) {
      setStartedOnMobile(true);
    }

    if (WS_EVENT_TYPES.ivCompletedContinuedOnMobile === message.event_type) {
      goToHome();
    }

    if (WS_EVENT_TYPES.ivCompletedReturningToDesktop === message.event_type) {
      handleReturningToDesktop();
    }

    return () => {
      websocket.clearMessage();
    };
  }, [goToHome, handleReturningToDesktop, setStartedOnMobile, websocket]);

  useEffect(() => () => setStartedOnMobile(false), [setStartedOnMobile]);
}
